<script setup lang="ts">
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { escapeHtmlAttribute } from '@/utils/general';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiSerpFaqProps {
  location?: any;
  searchResult: any;
}
const props = withDefaults(defineProps<CiSerpFaqProps>(), {
  location: () => {},
});

/********************
 * COMPOSITIONS      *
 ********************/
const getRouteBaseName = useRouteBaseName();
const jsonLdStore = useJsonLdStore();
const localePath = useLocalePath();
const route = useRoute();
const router = useRouter();
const runtimeConfig = useRuntimeConfig();
const { $gettext, interpolate, $pgettext } = useGettext();
const { delegateLinksToRouter } = useDelegateToRouter();
const { locale } = useI18n();

/********************
 * REFS & VARS       *
 ********************/
const baseUrl = runtimeConfig.public.baseUrl || 'https://www.camping.info';

const faq = computed(() => {
  return [
    getBestCampsiteQuestion(),
    getRecommendedCampsiteQuestion(),
    getAvgPriceQuestion(),
    getAvgPitchPriceQuestion(),
    getAvgAccommodationPriceQuestion(),
    getAvailableCampsitesQuestion(),
  ].filter(item => item);
});

const isRegionSearch = computed(() => {
  const baseRouteName = getRouteBaseName(route);
  return baseRouteName === RBN_REGION_SEARCH;
});

const i18nHeadline = computed(() => {
  return $gettext('Frequently asked questions');
});

/********************
 * INITIALIZATION    *
 ********************/
// faqs will only be added on SSR
if (import.meta.server) {
  const items = [];
  const envURL = baseUrl.replace(/\/+$/, '');

  faq.value.forEach((item) => {
    let txt = item.answer;
    if (item.link?.href && item.link?.text) {
      const targetUrl = `${envURL}${router.resolve(item.link.href).href}`;
      txt += `<br /><br /><a href="${targetUrl}">${escapeHtmlAttribute(item.link.text)}</a>`;
    }
    items.push({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': txt,
      },
    });
  });
  jsonLdStore.addFaqs({ items, faqId: `${baseUrl}${route.path}#faq` });
}

function getBestCampsiteQuestion() {
  if (!props.location?.name) {
    return null;
  }

  if (!props.location?.seo?.top_rated_campsites?.length) {
    return null;
  }

  const translatedQuestion = isRegionSearch.value
    ? $gettext('Which is the best campsite in the region %{searchTerm}?')
    : $gettext('Which is the best campsite in %{searchTerm}?');
  const question = interpolate(translatedQuestion, { searchTerm: props.location?.name }, true);

  const translatedAnswer = isRegionSearch.value
    ? $gettext('The best rated campsites in the region %{searchTerm} are:')
    : $gettext('The best rated campsites in %{searchTerm} are:');
  const answer = interpolate(translatedAnswer, { searchTerm: props.location?.name }, true);

  const campsitesAsList = props.location.seo.top_rated_campsites.reduce((accumulator, currentCampsite) => {
    const url = localePath({
      name: RBN_CAMPSITE_DETAIL,
      params: { identifier: currentCampsite.slug },
    });
    return `${accumulator}<li><a href="${url}">${currentCampsite.name}</a><span class="whitespace-nowrap"><span>, ${currentCampsite.rating_avg_overall}</span><span class="inline-flex icon ml-1 fill-warning" style="width: 17.5px;"><svg  viewBox="0 0 576 512" width="17.5" height="17.5" xmlns="http://www.w3.org/2000/svg"><path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"></path></svg></span></span></li>`;
  }, '');

  return {
    question,
    answer: `${answer} <br /><br /><ol class="m-1">${campsitesAsList}</ol>`,
    link: {
      text: $gettext('Show all'),
      href: {
        name: route.name,
        params: route.params,
        query: { order: 'rating' },
        hash: '#top',
      },
    },
  };
}

function getRecommendedCampsiteQuestion() {
  if (!props.location?.name) {
    return null;
  }

  const recommendedCampsites = props.searchResult.filter(campsite => campsite.rating_avg_overall > 3.8);

  if (!recommendedCampsites.length) {
    return null;
  }

  const translatedQuestion = isRegionSearch.value
    ? $gettext('Which campsite in the %{searchTerm} region is recommended?')
    : $gettext('Which campsite in %{searchTerm} is recommended?');
  const question = interpolate(translatedQuestion, { searchTerm: props.location?.name }, true);

  const translatedAnswer = isRegionSearch.value
    ? $gettext('These campsites in the region %{searchTerm} are recommended:')
    : $gettext('These campsites in %{searchTerm} are recommended:');
  const answer = interpolate(translatedAnswer, { searchTerm: props.location?.name }, true);

  const campsitesAsList = recommendedCampsites.slice(0, 3).reduce((accumulator, currentCampsite) => {
    const url = localePath({
      name: RBN_CAMPSITE_DETAIL,
      params: { identifier: currentCampsite.slug },
    });
    return `${accumulator}<li><a href="${url}">${currentCampsite.name}</a><span class="whitespace-nowrap"><span>, ${currentCampsite.rating_avg_overall}</span><span class="inline-flex icon ml-1 fill-warning" style="width: 17.5px;"><svg  viewBox="0 0 576 512" width="17.5" height="17.5" xmlns="http://www.w3.org/2000/svg"><path d="M316.9 18C311.6 7 300.4 0 288.1 0s-23.4 7-28.8 18L195 150.3 51.4 171.5c-12 1.8-22 10.2-25.7 21.7s-.7 24.2 7.9 32.7L137.8 329 113.2 474.7c-2 12 3 24.2 12.9 31.3s23 8 33.8 2.3l128.3-68.5 128.3 68.5c10.8 5.7 23.9 4.9 33.8-2.3s14.9-19.3 12.9-31.3L438.5 329 542.7 225.9c8.6-8.5 11.7-21.2 7.9-32.7s-13.7-19.9-25.7-21.7L381.2 150.3 316.9 18z"></path></svg></span></span></li>`;
  }, '');

  return {
    question,
    answer: `${answer} <br /><br /><ol class="m-1">${campsitesAsList}</ol>`,
    link: {
      text: $gettext('Show all'),
      href: {
        name: route.name,
        params: route.params,
        query: {},
        hash: '#top',
      },
    },
  };
}

function getAvgPriceQuestion() {
  if (!props.location?.seo?.avg_reference_price || !props.location?.name) {
    return null;
  }

  const translatedQuestion = isRegionSearch.value
    ? $gettext('How expensive is camping in the region %{searchTerm}?')
    : $gettext('How expensive is camping in %{searchTerm}?');
  const question = interpolate(translatedQuestion, { searchTerm: props.location?.name }, true);

  const translatedAnswer = isRegionSearch.value
    ? $gettext(
        'An overnight stay at a campsite in the region %{searchTerm} costs on average %{price}. Prices vary depending on the season and the facilities and location of the campsite.',
      )
    : $gettext(
        'An overnight stay at a campsite in %{searchTerm} costs on average %{price}. Prices vary depending on the season and the facilities and location of the campsite.',
      );

  const price = new Intl.NumberFormat(locale.value, { style: 'currency', currency: 'EUR' }).format(
    props.location.seo.avg_reference_price,
  );
  const answer = interpolate(translatedAnswer, { searchTerm: props.location?.name, price }, true);

  return {
    question,
    answer,
    link: {
      text: $pgettext('show prices faq', 'Show current prices now'),
      href: {
        name: route.name,
        params: route.params,
        query: { dyndates: 'sa,1w,1w', flex: 3 },
        hash: '#top',
      },
    },
  };
}

function getAvgPitchPriceQuestion() {
  if (!props.location?.seo?.avg_pitch_price || !props.location?.name) {
    return null;
  }

  const translatedQuestion = isRegionSearch.value
    ? $gettext('How much does a pitch cost at a campsite in the %{searchTerm} region?')
    : $gettext('How much does a pitch cost at a campsite in %{searchTerm}?');
  const question = interpolate(translatedQuestion, { searchTerm: props.location?.name }, true);

  const translatedAnswer = isRegionSearch.value
    ? $gettext(
        'Pitches at campsites in the region %{searchTerm} cost on average %{price}. Prices vary according to season, facilities and location.',
      )
    : $gettext(
        'Pitches at campsites in %{searchTerm} cost on average %{price}. Prices vary according to season, facilities and location.',
      );

  const price = new Intl.NumberFormat(locale.value, { style: 'currency', currency: 'EUR' }).format(
    props.location.seo.avg_pitch_price,
  );
  const answer = interpolate(translatedAnswer, { searchTerm: props.location?.name, price }, true);

  return {
    question,
    answer,
    link: {
      text: $pgettext('show price pitches faq', 'Show current prices for pitches now'),
      href: {
        name: route.name,
        params: route.params,
        query: { dyndates: 'sa,1w,1w', flex: 3, acco_type: 'pitch' },
        hash: '#top',
      },
    },
  };
}

function getAvgAccommodationPriceQuestion() {
  if (!props.location?.seo?.avg_accommodation_price || !props.location?.name) {
    return null;
  }

  const translatedQuestion = isRegionSearch.value
    ? $gettext('What does rental accommodation cost at a campsite in the %{searchTerm} region?')
    : $gettext('What does rental accommodation cost at a campsite in %{searchTerm}?');
  const question = interpolate(translatedQuestion, { searchTerm: props.location?.name }, true);

  const translatedAnswer = isRegionSearch.value
    ? $gettext(
        'Rental accommodation in the region %{searchTerm} costs on average %{price}. Prices vary according to season as well as facilities and location.',
      )
    : $gettext(
        'Rental accommodation in %{searchTerm} costs on average %{price}. Prices vary according to season as well as facilities and location.',
      );

  const price = new Intl.NumberFormat(locale.value, { style: 'currency', currency: 'EUR' }).format(
    props.location?.seo?.avg_accommodation_price,
  );
  const answer = interpolate(translatedAnswer, { searchTerm: props.location?.name, price }, true);

  return {
    question,
    answer,
    link: {
      text: $pgettext('show price rentals faq', 'Show current prices for rental accommodation now'),
      href: {
        name: route.name,
        params: route.params,
        query: { dyndates: 'sa,1w,1w', flex: 3, acco_type: 'acco' },
        hash: '#top',
      },
    },
  };
}

function getAvailableCampsitesQuestion() {
  if (!props.location?.name) {
    return null;
  }

  const translatedQuestion = isRegionSearch.value
    ? $gettext('Which campsites are available in the region %{searchTerm}?')
    : $gettext('Which campsites are available in %{searchTerm}?');

  const question = interpolate(translatedQuestion, { searchTerm: props.location?.name }, true);

  const translatedAnswer = isRegionSearch.value
    ? $gettext(
        'The occupancy rate of campsites in the %{searchTerm} region is very much dependent on the travel period. During holidays and public holidays, campsites are often fully booked well in advance. The location and popularity of a campsite as well as the weather are further factors. We therefore recommend looking for availability early and booking directly online. For short-term camping holidays in the %{searchTerm} region, we offer you the option of searching for currently available campsites in real time.',
      )
    : $gettext(
        'The occupancy rate of campsites in the %{searchTerm} is very much dependent on the travel period. During holidays and public holidays, campsites are often fully booked well in advance. The location and popularity of a campsite as well as the weather are further factors. We therefore recommend looking for availability early and booking directly online. For short-term camping holidays in the %{searchTerm}, we offer you the option of searching for currently available campsites in real time.',
      );

  const answer = interpolate(translatedAnswer, { searchTerm: props.location?.name }, true);
  const translatedLinkText = isRegionSearch.value
    ? $gettext('Show available campsites in the region %{searchTerm}')
    : $gettext('Show available campsites in %{searchTerm}');
  const linkText = interpolate(translatedLinkText, { searchTerm: props.location?.name }, true);
  return {
    question,
    answer,
    link: {
      text: linkText,
      href: {
        name: route.name,
        params: route.params,
        query: { dyndates: 'sa,1w,1w', flex: 3 },
        hash: '#top',
      },
    },
  };
}
</script>

<template>
  <div>
    <h2
      key="serp-headline-faq"
      class="mb-2 px-6 text-center text-black"
      v-html="i18nHeadline"
    />

    <template
      v-for="(item, index) in faq"
      :key="`faq-${index}`"
    >
      <details class="details">
        <summary class="accordion__header flex flex-row gap-[30px] px-[15px]">
          <h3 class="m-0 py-6 text-black max-md:text-base max-md:font-medium max-md:leading-6">
            {{ item.question }}
          </h3>
          <CiAwesomeIcon
            :icon="faChevronDown"
            class="accordion__icon ml-auto self-center fill-primary transition-transform duration-[250ms] ease-in"
            ratio="0.75"
          />
        </summary>
        <div class="container pb-2">
          <div class="row">
            <div class="col-12 col-lg-10 mb-2 text-black">
              <div
                @click="delegateLinksToRouter"
                v-html="item.answer"
              />
              <nuxt-link
                v-if="item.link && item.link.href"
                class="mt-4 inline-block"
                :to="item.link.href"
              >{{
                item.link.text
              }}</nuxt-link>
            </div>
          </div>
        </div>
      </details>
    </template>
  </div>
</template>

<style></style>
